import { GameState } from './types';
import {
  createState, isOpen, addAndSort, findTileSlot,
  removeMatch, isTrayFull, isWon, findHint,
} from './game';
import { type Layout } from './types';
import {
  computeLayout, renderFrame, readTheme, loadSprites,
  tileAtPoint, tilePixelPos, hitCircle, spawnParticles,
  menuRects, inMenuRect, drawMenu,
} from './renderer';
import { attachTapListener } from './input';
import { track } from './analytics';
import {
  resumeAudio, playPick, playLand, playMatch, playWin, playLose, playHint,
} from './audio';

// ── Canvas setup ──────────────────────────────────────────────────────────
const canvas = document.getElementById('game') as HTMLCanvasElement;
const ctx = canvas.getContext('2d')!;
let W = 0, H = 0;

function resize(): void {
  const w = window.innerWidth;
  const h = window.innerHeight;
  if (!w || !h) return;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  W = w; H = h;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  canvas.style.width = `${W}px`;
  canvas.style.height = `${H}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}
resize();
new ResizeObserver(resize).observe(document.body);
document.addEventListener('visibilitychange', () => { if (!document.hidden) resize(); });
window.addEventListener('pageshow', e => { if (e.persisted) resize(); });

document.addEventListener('touchstart', resumeAudio, { once: true });
document.addEventListener('click', resumeAudio, { once: true });

// ── Прогресс ──────────────────────────────────────────────────────────────
const SAVE_KEY = 'game-zen-match-level';

function loadLevel(): number {
  try {
    const v = parseInt(localStorage.getItem(SAVE_KEY) ?? '', 10);
    return Number.isFinite(v) && v >= 0 ? v : 0;
  } catch { return 0; }
}

function saveLevel(n: number): void {
  try { localStorage.setItem(SAVE_KEY, String(n)); } catch { /* storage опционален */ }
}

// dev-хук: ?level=N — открыть уровень N (1-based), прогресс не пишется
const forcedLevel = parseInt(new URLSearchParams(location.search).get('level') ?? '', 10);

// ── State ─────────────────────────────────────────────────────────────────
let state: GameState = createState(forcedLevel >= 1 ? forcedLevel - 1 : loadLevel());

// ── Analytics (события раунда) ────────────────────────────────────────────
let ended = false; // level_complete / game_over шлём один раз на раунд
function reportStart(): void {
  ended = false;
  track('game_start');
}
reportStart(); // первый уровень (boot)

const theme = readTheme();
let sprites = await loadSprites();
let layout: Layout = computeLayout(W, H, state.level);

// ── Menu (☰ → overlay: Продолжить / Заново / В хаб) ──────────────────────
let menuOpen = false;

// ── Input ─────────────────────────────────────────────────────────────────
attachTapListener(canvas, (x, y) => {
  resumeAudio();
  layout = computeLayout(W, H, state.level);
  const now = performance.now();

  // Menu tap-intercept — BEFORE any tile/undo/hint/overlay logic
  const m = menuRects(W, H);
  if (menuOpen) {
    if (inMenuRect(x, y, m.resume))  { menuOpen = false; return; }
    if (inMenuRect(x, y, m.restart)) { menuOpen = false; state = createState(state.level); reportStart(); return; }
    if (inMenuRect(x, y, m.hub))     { location.href = document.body.dataset.hubUrl ?? '/'; return; }
    menuOpen = false; return; // тап мимо пунктов — закрыть
  }
  if (state.phase === 'playing' &&
      hitCircle(x, y, m.toggle.cx, m.toggle.cy, m.toggle.r + 6)) {
    menuOpen = true; return;
  }

  // Won overlay → next level
  if (state.phase === 'won') {
    const nextLevel = state.level + 1;
    state = createState(nextLevel);
    reportStart();
    return;
  }

  // Lost overlay → restart same level
  if (state.phase === 'lost') {
    state = createState(state.level);
    reportStart();
    return;
  }

  // Block taps during animations
  if (state.flyAnim || state.matchAnim) return;

  // Undo button
  if (hitCircle(x, y, layout.undoBtnX, layout.undoBtnY, layout.btnR)) {
    if (state.undo !== null) {
      state = {
        ...state,
        fieldTiles: state.undo.fieldTiles,
        tray: state.undo.tray,
        undo: null,
        pending: null,
        hintTileId: null,
      };
    }
    return;
  }

  // Hint button
  if (hitCircle(x, y, layout.hintBtnX, layout.hintBtnY, layout.btnR)) {
    const hintTile = findHint(state);
    if (hintTile) {
      playHint();
      state = { ...state, hintTileId: hintTile.id, hintStartMs: now };
    }
    return;
  }

  // Field tile tap
  const tile = tileAtPoint(x, y, state, layout);
  if (tile && isOpen(tile, state.fieldTiles)) {
    playPick();

    // Save undo snapshot before mutating
    const undoSnapshot = {
      fieldTiles: state.fieldTiles.map(t => ({ ...t })),
      tray: [...state.tray],
    };

    // Calculate destination before removing tile from field
    const { tray: newTray, matchType } = addAndSort(state.tray, tile);
    const toSlot = findTileSlot(newTray, tile);

    const fromPos = tilePixelPos(tile, layout);

    state = {
      ...state,
      fieldTiles: state.fieldTiles.filter(t => t.id !== tile.id),
      pending: { tray: newTray, matchType },
      hintTileId: null,
      undo: undoSnapshot,
      flyAnim: {
        tile,
        fromX: fromPos.x,
        fromY: fromPos.y,
        toSlot,
        startMs: now,
        durationMs: 300,
      },
    };
  }
});

// ── RAF loop ──────────────────────────────────────────────────────────────

function loop(now: number): void {
  try {
    layout = computeLayout(W, H, state.level);

    // A stale open menu must not swallow the next tap on a win/lost overlay
    if (state.phase !== 'playing' && menuOpen) menuOpen = false;

    // Fly animation completes → land tile in tray
    if (state.flyAnim && now - state.flyAnim.startMs >= state.flyAnim.durationMs) {
      playLand();
      const { tray: newTray, matchType } = state.pending!;
      state = { ...state, flyAnim: null, tray: newTray, pending: null };

      if (matchType !== null) {
        const slots = newTray.reduce<number[]>(
          (acc, s, i) => { if (s?.type === matchType) acc.push(i); return acc; },
          [],
        );
        state = {
          ...state,
          matchAnim: { type: matchType, slots: slots.slice(0, 3), startMs: now, durationMs: 450 },
        };
      } else if (isTrayFull(newTray)) {
        playLose();
        state = { ...state, phase: 'lost' };
        if (!ended) { ended = true; track('game_over', { result: 'lose', level: state.level }); }
      }
    }

    // Match animation completes → remove tiles, spawn particles, check win
    if (state.matchAnim && now - state.matchAnim.startMs >= state.matchAnim.durationMs) {
      const { type, slots } = state.matchAnim;
      playMatch();

      const newParticles = spawnParticles(slots, layout, type);
      const newTray = removeMatch(state.tray, type);

      state = {
        ...state,
        tray: newTray,
        matchAnim: null,
        particles: [...state.particles, ...newParticles],
      };

      if (isWon(state)) {
        playWin();
        state = { ...state, phase: 'won' };
        if (!(forcedLevel >= 1)) saveLevel(state.level + 1);
        if (!ended) { ended = true; track('level_complete', { level: state.level }); }
      }
    }

    // Expire finished particles
    if (state.particles.length > 0) {
      state = {
        ...state,
        particles: state.particles.filter(p => now - p.startMs < p.durationMs),
      };
    }

    // Expire hint
    if (state.hintTileId !== null && now - state.hintStartMs >= 2000) {
      state = { ...state, hintTileId: null };
    }

    renderFrame(ctx, layout, state, theme, sprites, now);
    drawMenu(ctx, W, H, state.phase, menuOpen, theme);
  } catch (e) {
    console.error('[zen-match] loop error', e);
  }
  requestAnimationFrame(loop);
}

requestAnimationFrame(loop);
